import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Resolve Inbound Email For Staged Purchase Order Step
 *
 * Looks up the inbound email a staged purchase order was created from and
 * returns the Microsoft Graph message ID + the mailbox the message was
 * received in.
 *
 * The link between an order and its source email lives on the staged PO via
 * its `source` + `sourceId` columns: when `source === email`, `sourceId` is
 * the inbound email id.
 *
 * Wired upstream of `send_email` to enable native Graph forward (the original
 * customer email's body and attachments are re-attached server-side, so we
 * never re-upload bytes).
 *
 * Returns nulls when the staged PO has no linked inbound email (EDI, manual,
 * portal). Downstream `send_email` then sends without forward.
 */
export const resolveInboundEmailForStagedPurchaseOrderStep = defineStepConfig({
  type: "resolve_inbound_email_for_staged_purchase_order",
  name: "Resolve Inbound Email For Staged Purchase Order",
  description:
    "Resolves the source inbound email for a staged purchase order so a downstream send_email can forward it via Microsoft Graph (preserving original attachments).",
  category: "Data Access",
  deprecated: {
    reason:
      "Legacy staged-PO flow. The OMS v3 draft-sales-order flow carries the inbound email + documents through its own steps; this staged-PO email resolver is no longer used.",
  },

  inputSchema: z.object({
    stagedPurchaseOrderId: z
      .uuid()
      .nullable()
      .describe(
        "The staged purchase order whose source inbound email should be resolved. If null, or if the staged PO did not originate from email, the step returns nulls.",
      ),
  }),

  outputSchema: z.object({
    graphMessageId: z
      .string()
      .nullable()
      .describe(
        "Microsoft Graph internal message ID (AAMk... format) of the source inbound email. Null if the order has no inbound email or the message ID is missing.",
      ),
    sourceMailbox: z
      .string()
      .nullable()
      .describe(
        "Mailbox UPN that received the source email. Null when there is no source email. Callers must verify it matches the configured sender mailbox before forwarding.",
      ),
    inboundEmailId: z
      .uuid()
      .nullable()
      .describe(
        "ID of the linked InboundEmail row, if any. Useful for audit and debugging.",
      ),
  }),

  configSchema: z.object({}),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 3,
    },
  },
});
